import { sortBy } from 'lodash';
import { useIntl } from 'react-intl';
import { Image, Popover, Tooltip, Typography } from 'antd';
import { StockItem } from '@/core/domain/stock/StockItems/StockItems.types';
import { usePartConfigurationProfile } from '@/core/domain/stock/Configuration/Configuration.hooks';
import {
  PartItemFieldOption,
  PartItemFieldOptions,
  PartItemFieldsEnum,
} from '@/core/domain/stock/Configuration/Configuration.types';
import { ColumnsType, Group } from '@/components/_common';
import { quantityFormatter } from '@/utils/number';
import { COLUMNS_WIDTH } from '@/utils/table';
import { Info } from 'react-feather';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import formatterPrice from '@/utils/number/formatterPrice';
import { DeploymentUnitOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { FaEye, FaImage } from 'react-icons/fa';

type Params = {
  hasSorter?: boolean;
  showImage?: boolean;
  enabled?: boolean;
};

function usePartsColumns(params?: Params): {
  columns: ColumnsType<StockItem>;
  isFetching: boolean;
  locationColumn: ColumnsType<StockItem>;
} {
  const intl = useIntl();
  const { data, isFetching } = usePartConfigurationProfile(params?.enabled);

  const configData = data?.result;

  const hasSorter = params?.hasSorter !== undefined ? params?.hasSorter : true;

  const renderDescriptionWithImage = (item: StockItem) => (
    <Group gap={16}>
      <Image
        width={40}
        height={40}
        src={item.imageUrl}
        wrapperStyle={{ flexShrink: 0 }}
        placeholder={
          <Group position="center" style={{ height: '100%' }}>
            <FaImage color="lightgray" />
          </Group>
        }
        preview={item.imageUrl ? { mask: <FaEye /> } : false}
      />
      {renderDescription(item)}
    </Group>
  );

  const renderDescription = (item: StockItem) => (
    <Tooltip title={item?.observation} trigger={['hover', 'click']}>
      <Typography.Text
        title={item.description}
        ellipsis
        style={{ whiteSpace: 'pre' }}
      >
        {item.description || '-'}
      </Typography.Text>
    </Tooltip>
  );

  const locationColumn: ColumnsType<StockItem> = [
    {
      key: PartItemFieldOption.location,
      title: 'Localização',
      align: 'center',
      width: 60,
      ellipsis: true,
      render: (_, record: StockItem) => {
        if (!record.prices) return '-';
        if (record.prices.length === 1) return record.prices[0].location || '-';

        return (
          <Popover
            overlayInnerStyle={{
              maxWidth: 700,
              maxHeight: 200,
              overflowY: 'auto',
            }}
            content={record.prices.map((prices, index) => (
              <Group
                key={index}
                position="between"
                style={{
                  marginBottom: index + 1 < record.prices?.length ? '8px' : 0,
                }}
              >
                <span>{prices.company.name}:&nbsp;</span>
                <span>{prices?.location || '-'}</span>
              </Group>
            ))}
          >
            <EnvironmentOutlined style={{ fontSize: '20px' }} />
          </Popover>
        );
      },
    },
  ];

  const columns: ColumnsType<StockItem> = [
    {
      key: PartItemFieldOption.Description,
      title: intl.formatMessage({
        id: `settings.partsConfiguration.${PartItemFieldOption.Description}`,
      }),
      dataIndex: 'description',
      width: COLUMNS_WIDTH.billToPay,
      ellipsis: true,
      render: (_, record) => {
        const configDataImage = configData?.find(
          (a) => a.field === PartItemFieldsEnum.image,
        );

        const shouldShowImage = params?.showImage || configDataImage?.active;

        return shouldShowImage
          ? renderDescriptionWithImage(record)
          : renderDescription(record);
      },
      sorter: hasSorter,
    },
    {
      key: PartItemFieldOption.BarCode,
      title: intl.formatMessage({
        id: `settings.partsConfiguration.${PartItemFieldOption.BarCode}`,
      }),
      dataIndex: 'barcode',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      render: (value: string) => value || '-',
      sorter: hasSorter,
    },
    {
      key: PartItemFieldOption.OriginalCode,
      title: intl.formatMessage({
        id: `settings.partsConfiguration.${PartItemFieldOption.OriginalCode}`,
      }),
      dataIndex: 'sku',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      render: (value: string) => value || '-',
      sorter: hasSorter,
    },
    {
      key: PartItemFieldOption.ManufacturingCode,
      title: intl.formatMessage({
        id: `settings.partsConfiguration.${PartItemFieldOption.ManufacturingCode}`,
      }),
      dataIndex: 'manufacturingCode',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      render: (value: string) => value || '-',
      sorter: hasSorter,
    },
    {
      key: PartItemFieldOption.companyCode,
      title: intl.formatMessage({
        id: `settings.partsConfiguration.${PartItemFieldOption.companyCode}`,
      }),
      dataIndex: 'companyCode',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      render: (value: string) => value || '-',
      sorter: hasSorter,
    },
    {
      key: PartItemFieldOption.AvailableStock,
      title: intl.formatMessage({
        id: `settings.partsConfiguration.${PartItemFieldOption.AvailableStock}`,
      }),
      align: 'right',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      render: (value: unknown, record: StockItem) => {
        // TODO: Removendo total da soma, porem precisaremos exibir futuramente
        // const totalAvailableStock = record.stock?.reduce(
        //   (prev, curr) => prev + curr.availableStock,
        //   0,
        // );

        const sorted = sortBy(record.stock, (a) => a.company.id);

        return (
          <Popover
            overlayInnerStyle={{
              maxWidth: 700,
              maxHeight: 200,
              overflowY: 'auto',
            }}
            content={record.stock?.map((s, i) => (
              <Group
                key={s.company.id}
                style={{
                  marginBottom: i + 1 < record.stock?.length ? '8px' : 0,
                }}
              >
                <div>{s.company.name}:</div>

                <div style={{ marginLeft: 'auto', paddingLeft: 4 }}>
                  {quantityFormatter(s.availableStock)}
                </div>
              </Group>
            ))}
          >
            {sorted.length > 1 ? (
              <Info size={20} id="note-type-icon" cursor={'pointer'} />
            ) : (
              <>
                {sorted[0]?.availableStock === 0 ? (
                  <span className="span-stock-items-no-quantity">
                    {quantityFormatter(sorted[0]?.availableStock || 0)}
                  </span>
                ) : (
                  quantityFormatter(sorted[0]?.availableStock || 0)
                )}
              </>
            )}
          </Popover>
        );
      },
    },
    {
      key: PartItemFieldOption.TotalStock,
      title: intl.formatMessage({
        id: `settings.partsConfiguration.${PartItemFieldOption.TotalStock}`,
      }),
      dataIndex: 'totalStock',
      align: 'right',
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      render: (value: number) => quantityFormatter(value),
    },
    {
      key: PartItemFieldOption.Manufacturer,
      title: intl.formatMessage({
        id: `settings.partsConfiguration.${PartItemFieldOption.Manufacturer}`,
      }),
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
      dataIndex: ['manufacturer', 'name'],
      render: (value: string) => value || '-',
      sorter: hasSorter,
    },
    {
      key: PartItemFieldOption.Group,
      title: intl.formatMessage({
        id: `settings.partsConfiguration.${PartItemFieldOption.Group}`,
      }),
      width: COLUMNS_WIDTH.small,
      ellipsis: true,
      dataIndex: ['group', 'name'],
      render: (value: string) => value || '-',
      sorter: hasSorter,
    },
    {
      key: PartItemFieldOption.SellPrice,
      title: intl.formatMessage({
        id: `settings.partsConfiguration.${PartItemFieldOption.SellPrice}`,
      }),
      align: 'center',
      width: COLUMNS_WIDTH.extraSmall,
      ellipsis: true,
      render: (_value, record: StockItem) => {
        if (!hasPermissions([Authorities.REGISTER_PART_VIEW_PRICES]))
          return '-';

        const sorted = sortBy(record.prices, (a) => a.company.id);

        const matrixPrice = sorted[0];

        if (!matrixPrice) return '-';

        return (
          <Popover
            overlayInnerStyle={{
              maxWidth: 700,
              maxHeight: 200,
              overflowY: 'auto',
            }}
            content={sorted.map((s, i) => (
              <Group
                key={s.company.id}
                style={{
                  marginBottom: i + 1 < record.stock?.length ? '8px' : 0,
                }}
              >
                <div>{s.company.name}:</div>
                <div style={{ marginLeft: 'auto', paddingLeft: 4 }}>
                  R${quantityFormatter(s.price)}
                </div>
              </Group>
            ))}
          >
            {sorted.length > 1 ? (
              <Info size={20} id="note-type-icon" cursor={'pointer'} />
            ) : (
              formatterPrice(matrixPrice.price)
            )}
          </Popover>
        );
      },
    },
    {
      key: PartItemFieldOption.applications,
      title: 'Aplicações',
      align: 'center',
      width: 60,
      ellipsis: true,
      render: (_, record) => (
        <Popover
          overlayInnerStyle={{
            maxWidth: 700,
            maxHeight: 200,
            overflowY: 'auto',
          }}
          content={record?.applications?.map((application, index) => (
            <Group
              key={index}
              position="between"
              style={{
                marginBottom: index + 1 < record.applications?.length ? 8 : 0,
              }}
            >
              <span>{application.description || '-'}:&nbsp;</span>

              <span style={{ whiteSpace: 'nowrap' }}>
                {`${application.fromYear || ''} - ${application.toYear || ''}`}
              </span>
            </Group>
          ))}
        >
          <DeploymentUnitOutlined style={{ fontSize: 20, cursor: 'pointer' }} />
        </Popover>
      ),
    },
    ...locationColumn,
  ];

  const activeColumns = columns.filter((item) =>
    configData?.some(
      (config) =>
        (config.active && item.key === PartItemFieldOptions[config.field]) ||
        item.key === 'actions',
    ),
  );

  return { columns: activeColumns, isFetching, locationColumn };
}

export default usePartsColumns;
